/**
 * KT Loyalty Backend System
 * Manages points calculation, storage, and customer loyalty data
 */

class KTLoyaltyBackend {
  constructor() {
    // Use unified configuration
    this.config = {
      pointsMultiplier: 5, // Points per £1 spent
      tiers: {
        'Bronze': { minSpent: 0, pointsNeeded: 100, discount: 5 },
        'Silver': { minSpent: 100, pointsNeeded: 250, discount: 10 },
        'Gold': { minSpent: 250, pointsNeeded: 500, discount: 15 },
        'Platinum': { minSpent: 500, pointsNeeded: 1000, discount: 20 }
      },
      firebase: {
        projectId: "window.__ENV__.FIREBASE_PROJECT_ID",
        functionsBaseUrl: "https://us-central1-window.__ENV__.FIREBASE_PROJECT_ID.cloudfunctions.net"
      }
    };
    
    this.pointsMultiplier = this.config.pointsMultiplier;
    this.tiers = this.config.tiers;
  }

  // Calculate points earned from order
  calculatePointsEarned(orderTotal) {
    return Math.floor(orderTotal * this.pointsMultiplier);
  }

  // Calculate customer tier based on total spent
  calculateTier(totalSpent) {
    if (totalSpent >= 500) return 'Platinum';
    if (totalSpent >= 250) return 'Gold';
    if (totalSpent >= 100) return 'Silver';
    return 'Bronze';
  }

  // Calculate progress to next tier
  calculateProgress(currentPoints, currentTier) {
    const tier = this.tiers[currentTier];
    if (!tier) return 0;
    
    const progress = (currentPoints / tier.pointsNeeded) * 100;
    return Math.min(progress, 100);
  }

  // Update customer loyalty data
  async updateCustomerLoyalty(customerId, orderData) {
    try {
      const pointsEarned = this.calculatePointsEarned(orderData.total);
      
      // Get current customer data
      const currentData = await this.getCustomerLoyaltyData(customerId);
      
      // Update loyalty data
      const updatedData = {
        points: (currentData.points || 0) + pointsEarned,
        total_spent: (currentData.total_spent || 0) + orderData.total,
        orders_count: (currentData.orders_count || 0) + 1,
        last_order_date: new Date().toISOString(),
        tier: this.calculateTier((currentData.total_spent || 0) + orderData.total)
      };
      
      // Calculate new progress
      updatedData.progress = this.calculateProgress(updatedData.points, updatedData.tier);
      
      // Save to Shopify metafields
      await this.saveCustomerLoyaltyData(customerId, updatedData);
      
      return updatedData;
    } catch (error) {
      console.error('Error updating customer loyalty:', error);
      throw error;
    }
  }

  // Get customer loyalty data
  async getCustomerLoyaltyData(customerId) {
    try {
      // Try to get real data from Shopify metafields first
      if (window.Shopify && window.Shopify.customer) {
        const customer = window.Shopify.customer;
        if (customer && customer.id) {
          // Get loyalty data from customer metafields
          const loyaltyData = {
            points: parseInt(customer.metafields?.loyalty?.points?.value || 0),
            total_spent: parseFloat(customer.metafields?.loyalty?.total_spent?.value || 0),
            orders_count: parseInt(customer.metafields?.loyalty?.orders_count?.value || 0),
            tier: customer.metafields?.loyalty?.tier?.value || 'Bronze',
            progress: parseInt(customer.metafields?.loyalty?.progress?.value || 0),
            points_redeemed: parseInt(customer.metafields?.loyalty?.points_redeemed?.value || 0),
            total_discounts: parseFloat(customer.metafields?.loyalty?.total_discounts?.value || 0)
          };
          
          // Cache the data in localStorage
          localStorage.setItem(`kt_loyalty_${customerId}`, JSON.stringify(loyaltyData));
          return loyaltyData;
        }
      }
      
      // Fallback to localStorage
      const stored = localStorage.getItem(`kt_loyalty_${customerId}`);
      return stored ? JSON.parse(stored) : {
        points: 0,
        total_spent: 0,
        orders_count: 0,
        tier: 'Bronze',
        progress: 0,
        points_redeemed: 0,
        total_discounts: 0
      };
    } catch (error) {
      console.error('Error getting customer loyalty data:', error);
      return {
        points: 0,
        total_spent: 0,
        orders_count: 0,
        tier: 'Bronze',
        progress: 0,
        points_redeemed: 0,
        total_discounts: 0
      };
    }
  }

  // Save customer loyalty data
  async saveCustomerLoyaltyData(customerId, data) {
    try {
      // This would typically call Shopify Admin API
      // For now, we'll use localStorage as a fallback
      localStorage.setItem(`kt_loyalty_${customerId}`, JSON.stringify(data));
      
      // In production, this would update Shopify metafields:
      // await shopify.metafield.update({
      //   resource: 'customer',
      //   resourceId: customerId,
      //   namespace: 'loyalty',
      //   key: 'points',
      //   value: data.points.toString(),
      //   type: 'number_integer'
      // });
      
      return true;
    } catch (error) {
      console.error('Error saving customer loyalty data:', error);
      throw error;
    }
  }

  // Redeem points for discount
  async redeemPoints(customerId, pointsToRedeem) {
    try {
      const customerData = await this.getCustomerLoyaltyData(customerId);
      
      if (customerData.points < pointsToRedeem) {
        throw new Error('Insufficient points');
      }
      
      // Calculate discount amount (1 point = £0.01)
      const discountAmount = pointsToRedeem * 0.01;
      
      // Update customer data
      const updatedData = {
        ...customerData,
        points: customerData.points - pointsToRedeem,
        points_redeemed: (customerData.points_redeemed || 0) + pointsToRedeem,
        total_discounts: (customerData.total_discounts || 0) + discountAmount
      };
      
      // Recalculate progress
      updatedData.progress = this.calculateProgress(updatedData.points, updatedData.tier);
      
      // Save updated data
      await this.saveCustomerLoyaltyData(customerId, updatedData);
      
      return {
        success: true,
        discountAmount: discountAmount,
        remainingPoints: updatedData.points,
        discountCode: this.generateDiscountCode(discountAmount)
      };
    } catch (error) {
      console.error('Error redeeming points:', error);
      throw error;
    }
  }

  // Generate discount code
  generateDiscountCode(discountAmount) {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `LOYALTY${timestamp}${random}`.toUpperCase();
  }

  // Get available rewards for customer
  getAvailableRewards(customerPoints, customerTier) {
    const rewards = [];
    
    // Base rewards
    if (customerPoints >= 50) {
      rewards.push({
        id: 'discount_5',
        name: '£5 Off',
        points: 50,
        discount: 5,
        description: 'Get £5 off your next order'
      });
    }
    
    if (customerPoints >= 100) {
      rewards.push({
        id: 'discount_10',
        name: '£10 Off',
        points: 100,
        discount: 10,
        description: 'Get £10 off your next order'
      });
    }
    
    if (customerPoints >= 250) {
      rewards.push({
        id: 'discount_25',
        name: '£25 Off',
        points: 250,
        discount: 25,
        description: 'Get £25 off your next order'
      });
    }
    
    // Tier-specific rewards
    if (customerTier === 'Gold' || customerTier === 'Platinum') {
      rewards.push({
        id: 'free_shipping',
        name: 'Free Shipping',
        points: 75,
        discount: 'shipping',
        description: 'Free shipping on your next order'
      });
    }
    
    if (customerTier === 'Platinum') {
      rewards.push({
        id: 'priority_support',
        name: 'Priority Support',
        points: 50,
        discount: 'support',
        description: 'Priority customer support for 30 days'
      });
    }
    
    return rewards;
  }

  // Process order completion
  async processOrderCompletion(orderData) {
    try {
      if (!orderData.customer_id) {
        if (window.logger) {
          window.logger.log('Order has no customer ID, skipping loyalty update');
        }
        return;
      }
      
      const updatedLoyalty = await this.updateCustomerLoyalty(orderData.customer_id, orderData);
      
      // Send notification to customer
      await this.sendLoyaltyNotification(orderData.customer_id, updatedLoyalty);
      
      return updatedLoyalty;
    } catch (error) {
      console.error('Error processing order completion:', error);
      throw error;
    }
  }

  // Send loyalty notification
  async sendLoyaltyNotification(customerId, loyaltyData) {
    try {
      // This would typically send an email or push notification
      if (window.logger) {
        window.logger.log(`Loyalty notification sent to customer ${customerId}:`, loyaltyData);
      }
      
      // Example notification content
      const notification = {
        customerId: customerId,
        type: 'points_earned',
        title: 'Points Earned!',
        message: `You earned ${loyaltyData.points} loyalty points!`,
        data: loyaltyData
      };
      
      // In production, this would integrate with email service
      // await emailService.sendLoyaltyNotification(notification);
      
      return true;
    } catch (error) {
      console.error('Error sending loyalty notification:', error);
      throw error;
    }
  }

  // Get loyalty statistics
  async getLoyaltyStats() {
    try {
      // This would typically query the database
      // For now, return mock data
      return {
        totalCustomers: 1250,
        totalPointsIssued: 45600,
        totalPointsRedeemed: 23400,
        averagePointsPerCustomer: 36,
        topTier: 'Gold',
        activeCustomers: 890
      };
    } catch (error) {
      console.error('Error getting loyalty stats:', error);
      throw error;
    }
  }
}

// Initialize loyalty backend
window.KTLoyaltyBackend = new KTLoyaltyBackend();

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = KTLoyaltyBackend;
}


